import { useState } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { useIsManagerOrAdmin } from '../../../hooks/useCurrentUser';
import { ticketErrorRO } from '../ticketCopy';
import { useAddLabelMutation, useRemoveLabelMutation } from '../tickets.api';
import { type TicketLabel } from '../tickets.schema';
import { useLabelColorMap } from '../useLabelColors';

import { LabelChip } from './LabelChip';
import { LabelColorDialog } from './LabelColorDialog';
import { LabelInput } from './LabelInput';

export function LabelsPanel({ id, labels }: { id: number; labels: TicketLabel[] }) {
    const [addLabel, { isLoading: adding }] = useAddLabelMutation();
    const [removeLabel] = useRemoveLabelMutation();
    const colorMap = useLabelColorMap();
    const canRecolor = useIsManagerOrAdmin();
    const [editing, setEditing] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<TicketLabel | null>(null);

    const onAdd = async (label: string) => {
        try {
            await addLabel({ id, label }).unwrap();
        } catch (e) {
            toast.error(ticketErrorRO(e, 'Eroare la adaugarea etichetei.'));
        }
    };

    const onRemove = async () => {
        if (!pendingDelete) return;
        try {
            await removeLabel({ id, labelId: pendingDelete.id }).unwrap();
        } catch (e) {
            toast.error(ticketErrorRO(e, 'Eroare la stergerea etichetei.'));
        } finally {
            setPendingDelete(null);
        }
    };

    return (
        <div>
            <h2 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">Etichete</h2>
            {labels.length > 0 ? (
                <div className="mb-2 flex flex-wrap gap-2">
                    {labels.map((l) => (
                        <LabelChip
                            key={l.id}
                            label={l.label}
                            colorKey={colorMap[l.label.toLowerCase()]}
                            onRemove={() => setPendingDelete(l)}
                            onClick={canRecolor ? () => setEditing(l.label) : undefined}
                        />
                    ))}
                </div>
            ) : (
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">Nicio eticheta inca.</p>
            )}
            {canRecolor && labels.length > 0 && (
                <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">
                    Apasa pe o eticheta ca sa-i schimbi culoarea.
                </p>
            )}
            <LabelInput existing={labels.map((l) => l.label)} onAdd={onAdd} disabled={adding} />

            {canRecolor && (
                <LabelColorDialog
                    label={editing}
                    current={editing ? colorMap[editing.toLowerCase()] : null}
                    open={editing !== null}
                    onOpenChange={(o) => {
                        if (!o) setEditing(null);
                    }}
                />
            )}

            <ConfirmDialog
                open={pendingDelete !== null}
                onClose={() => setPendingDelete(null)}
                onConfirm={onRemove}
                title="Sterge eticheta?"
                description={pendingDelete ? `Eticheta "${pendingDelete.label}" va fi scoasa de pe acest ticket.` : ''}
                confirmLabel="Sterge"
                variant="danger"
            />
        </div>
    );
}
